import { useCallback } from 'react';

import { type Collectible, type NewOrderItem } from '@pkg/graphql/codegen/graphql';
import { type PrepareOrderType } from '@pkg/hooks/marketplace/usePrepareOrder';

import { useOrdersStore } from '@app/store/zustand';

import { ORDER_BUY, ORDER_LIST, ORDER_OFFER, ORDER_SELL } from './constants';

const orderSettingsByType = {
    buy: ORDER_BUY,
    offer: ORDER_OFFER,
    sell: ORDER_SELL,
    list: ORDER_LIST,
};

export function useNewOrder() {
    const { setListingOrderRequest, addToken } = useOrdersStore();

    const handlePrepareArguments = useCallback(
        async (items: Collectible[], orderType: PrepareOrderType) => {
            const item = items?.[0]; // store is based on 1 right now
            const orderSettings = orderSettingsByType[orderType];
            const chain = item?.id?.split('.')?.[0] ?? 'ethereum';
            addToken(item, orderType);
            const account = orderType === 'sell' || orderType === 'list' ? item?.owner?.address : undefined;

            const initialOrderItems = items?.map((item) => ({
                collectibleIdentifier: item.id,
                quantity: 1,
            })) as NewOrderItem[];

            const requestData = {
                ...orderSettings,
                items: initialOrderItems,
                account,
                chain,
            };

            setListingOrderRequest(requestData);
        },
        [addToken, setListingOrderRequest],
    );

    const buy = (items: Collectible[]) => handlePrepareArguments(items, 'buy');
    const offer = (items: Collectible[]) => handlePrepareArguments(items, 'offer');

    // owner items
    const list = (items: Collectible[]) => handlePrepareArguments(items, 'list');
    const sell = (items: Collectible[]) => handlePrepareArguments(items, 'sell');

    return { list, buy, sell, offer };
}
